import React from 'react';
import clsx from 'clsx';

type ColorSwatchProps = {
  hexColors: string[];
  dataFfid: string;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  value?: string;
  isProductTile?: boolean;
  variantSelected?: boolean;
  variantDisabled?: boolean;
  allowSelectingDisabled?: boolean;
};

const ColorSwatch: React.FC<ColorSwatchProps> = ({
  hexColors,
  dataFfid,
  onClick,
  value,
  isProductTile = false,
  variantSelected = false,
  variantDisabled = false,
  allowSelectingDisabled = false,
}) => {
  const [primaryHexColor, secondaryHexColor] = hexColors.map((hexColor) =>
    (hexColor ?? '').startsWith('#') ? hexColor : `#${hexColor}`,
  );

  const disabled = !allowSelectingDisabled && (variantDisabled || !onClick);
  const shouldShowStrikeThrough = variantDisabled || value === 'none';

  // There are quite a few visual differences between the PDP and product tiles, but I feel
  // there is too little code overall to warrant separate components for handling the visuals.

  let buttonClassName;
  let swatchClassName;
  let strikeThroughClassName;

  if (isProductTile) {
    buttonClassName = `size-30 ${variantSelected ? 'border border-1 border-black' : ''}`;
    swatchClassName = 'size-20 border border-1 border-[#858585] transition-transform group-active/swatch:scale-75 ';
    strikeThroughClassName = 'h-1 bg-[#858585]';
  } else {
    buttonClassName = `size-46 ${
      variantDisabled
        ? 'border border-2 border-[#858585]'
        : `border ${variantSelected ? 'border-[#333] p-4 variant-selected' : 'border-[#ADADAD]'}`
    }`;
    swatchClassName = `${variantSelected ? 'size-36' : variantDisabled ? 'size-42' : 'size-44'}`;
    strikeThroughClassName = `h-2 ${variantDisabled ? 'bg-[#858585]' : 'bg-[#ADADAD]'}`;
  }

  return (
    // The group is needed because scale on the button doesn't work, you can't click near its edges when it's applied
    <button
      type="button"
      title={value}
      disabled={disabled}
      role="radio"
      aria-label={value}
      aria-checked={variantSelected}
      data-ffid={dataFfid}
      onClick={onClick}
      className={clsx(
        'group/swatch pointer-events-auto flex shrink-0 items-center justify-center rounded-full disabled:pointer-events-none',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-black',
        buttonClassName,
      )}
    >
      <div
        style={{ background: secondaryHexColor ? `linear-gradient(135deg, ${primaryHexColor ?? '#fff'} 50%, ${secondaryHexColor} 50%)` : primaryHexColor ?? '#fff' }}
        className={clsx('relative rounded-full overflow-hidden', swatchClassName)}
      >
        {/* The strike-through always follows the swatch. It will overflow and be clipped, to nicely reach the edge. */}
        {shouldShowStrikeThrough && (
          <div
            className={clsx(
              'absolute left-1/2 top-1/2 w-[110%] -translate-x-1/2 -translate-y-1/2 rotate-45',
              strikeThroughClassName,
            )}
          />
        )}
      </div>
    </button>
  );
};

export default ColorSwatch;